import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft, Star, Trash2, MessageCircle, ChevronRight,
  Loader2, StarOff
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AGENT_NAMES = {
  routeur: "ODYS'GUIDE",
  analyse: "ODYS'ANALYSE",
  ia: "ODYS'ASSISTANT",
  strategie: "ODYS'STRATEGIE",
  nego: "ODYS'NEGO",
  banque: "ODYS'BANQUE",
  travaux: "ODYS'TRAVAUX",
  fiscalite: "ODYS'FISCALITE",
  locataire: "ODYS'LOCATAIRE",
  notaire: "ODYS'NOTAIRE",
  juridique: "ODYS'JURIDIQUE",
  marche: "ODYS'MARCHE",
  chasse: "ODYS'CHASSE",
  roundtable: "Reunion d'equipage",
};

const AGENT_COLORS = {
  routeur: "#16233B",
  analyse: "#FF8C42",
  ia: "#10B981",
  strategie: "#8B5CF6",
  nego: "#EF4444",
  banque: "#3B82F6",
  travaux: "#F59E0B",
  fiscalite: "#EC4899",
  locataire: "#06B6D4",
  notaire: "#7C3AED",
  juridique: "#0F766E",
  marche: "#0EA5E9",
  chasse: "#F97316",
  roundtable: "#B5894A",
};

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/api/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(res.data.favorites || []);
    } catch (err) {
      console.error("Error fetching favorites:", err);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (favoriteId) => {
    try {
      await axios.delete(`${API}/api/favorites/${favoriteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(favorites.filter(f => f.id !== favoriteId));
      toast.success("Favori supprime");
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-30 safe-top">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            <h1 className="font-semibold text-lg">Favoris</h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 safe-bottom">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-12">
            <StarOff className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold text-lg mb-2">Aucun favori</h3>
            <p className="text-muted-foreground mb-4">
              Sauvegarde tes analyses importantes pour les retrouver facilement
            </p>
            <Button onClick={() => navigate("/dashboard")}>
              Voir les experts
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {favorites.map((fav) => {
              const isRoundtable = fav.kind === "roundtable" || fav.agent_type === "roundtable";
              const targetUrl = isRoundtable
                ? `/reunion-equipage?id=${fav.conversation_id}`
                : `/agent/${fav.agent_type}?conversation=${fav.conversation_id}`;
              const displayName = AGENT_NAMES[fav.agent_type] || (isRoundtable ? "Reunion d'equipage" : "Agent IA");
              const bgColor = AGENT_COLORS[fav.agent_type] || (isRoundtable ? "#B5894A" : "#6B7280");
              return (
              <Card key={fav.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div 
                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(targetUrl)}
                    data-testid={`favorite-item-${fav.id}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                      style={{ backgroundColor: bgColor }}
                    >
                      {isRoundtable ? "TR" : (displayName.charAt(0) || "?")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{fav.title}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {displayName} • {formatDate(fav.created_at)}
                      </p>
                      {fav.summary && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {fav.summary}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                  </div>
                  <div className="border-t px-4 py-2 bg-muted/30 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFavorite(fav.id);
                      }}
                      data-testid={`favorite-remove-${fav.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
